import api from '../../config/axios';

// ============================================================================
// ADMIN SERVICE API
// All endpoints for admin-related operations
// ============================================================================

export const adminService = {

  // ============================================================================
  // USER MANAGEMENT API
  // ============================================================================
  
  /**
   * Get users list
   * GET /api/admin/users
   * Purpose: List all users with filtering and pagination
   * @param {Object} params - Query parameters for filtering
   * @returns {Promise} Response with users array
   */
  getUsers: async (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key]) queryParams.append(key, params[key]);
    });
    const response = await api.get(`/admin/users?${queryParams}`);
    return response.data;
  },

  // ============================================================================
  // CONTENT MANAGEMENT API
  // ============================================================================
  
  /**
   * Get content list
   * GET /api/admin/content
   * Purpose: List all content with filtering
   * @param {Object} params - Query parameters for filtering
   * @returns {Promise} Response with content array
   */
  getContent: async (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key]) queryParams.append(key, params[key]);
    });
    const response = await api.get(`/admin/content?${queryParams}`);
    return response.data;
  },

  /**
   * Create new content
   * POST /api/admin/:type
   * Purpose: Create new content item
   * @param {string} type - Content type (courses, subjects, etc.)
   * @param {Object} data - Content data
   * @returns {Promise} Response with created content
   */
  createContent: async (type, data) => {
    const response = await api.post(`/admin/${type}`, data);
    return response.data;
  },

  /**
   * Update content
   * PUT /api/admin/:type/:id
   * Purpose: Update existing content item
   * @param {string} type - Content type
   * @param {number} id - Content ID
   * @param {Object} data - Updated content data
   * @returns {Promise} Response with updated content
   */
  updateContent: async (type, id, data) => {
    // Check if data is FormData (for file uploads)
    const config = data instanceof FormData 
      ? { headers: { 'Content-Type': 'multipart/form-data' } }
      : {};
    
    const response = await api.put(`/admin/${type}/${id}`, data, config);
    return response.data;
  },

  /**
   * Delete content
   * DELETE /api/admin/:type/:id
   * Purpose: Remove content item from system
   * @param {string} type - Content type
   * @param {number} id - Content ID
   * @returns {Promise} Response confirming deletion
   */
  deleteContent: async (type, id) => {
    const response = await api.delete(`/admin/${type}/${id}`);
    return response.data;
  },

  // ============================================================================
  // SUBSCRIPTION MANAGEMENT API
  // ============================================================================
  
  /**
   * Get subscriptions list
   * GET /api/admin/subscriptions
   * Purpose: List all subscriptions with filtering
   * @param {Object} params - Query parameters for filtering
   * @returns {Promise} Response with subscriptions array
   */
  getSubscriptions: async (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key]) queryParams.append(key, params[key]);
    });
    const response = await api.get(`/admin/subscriptions?${queryParams}`);
    return response.data;
  },

  /**
   * Update subscription status
   * PUT /api/admin/subscriptions/:subscriptionId/status
   * Purpose: Change subscription status (active/cancelled/etc.)
   * @param {number} subscriptionId - ID of subscription
   * @param {Object} data - Status update data
   * @returns {Promise} Response confirming status update
   */
  updateSubscriptionStatus: async (subscriptionId, data) => {
    const response = await api.put(`/admin/subscriptions/${subscriptionId}/status`, data);
    return response.data;
  },

  // ============================================================================
  // PAYMENT REQUESTS API
  // ============================================================================
  
  /**
   * Get payment requests
   * GET /api/subscription-system/payment-requests
   * Purpose: List all payment requests with filtering
   * @param {Object} params - Query parameters for filtering
   * @returns {Promise} Response with payment requests array
   */
  getPaymentRequests: async (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key]) queryParams.append(key, params[key]);
    });
    const response = await api.get(`/subscription-system/payment-requests?${queryParams}`);
    return response.data;
  },

  /**
   * Update payment request status
   * PUT /api/subscription-system/payment-requests/:id/status
   * Purpose: Change payment request status (approved/rejected/etc.)
   * @param {number} id - Payment request ID
   * @param {Object} data - Status update data
   * @returns {Promise} Response confirming status update
   */
  updatePaymentRequestStatus: async (id, data) => {
    const response = await api.put(`/subscription-system/payment-requests/${id}/status`, data);
    return response.data;
  },

  // ============================================================================
  // ACTIVATION CODES API
  // ============================================================================
  
  /**
   * Get activation codes
   * GET /api/subscription-system/activation-codes
   * Purpose: List all activation codes
   * @returns {Promise} Response with activation codes array
   */
  getActivationCodes: async () => {
    const response = await api.get('/subscription-system/activation-codes');
    return response.data;
  },

  /**
   * Create activation codes
   * POST /api/subscription-system/activation-codes
   * Purpose: Generate new activation codes
   * @param {Object} data - Code generation parameters
   * @returns {Promise} Response with created codes
   */
  createActivationCodes: async (data) => {
    const response = await api.post('/subscription-system/activation-codes', data);
    return response.data;
  },

  // ============================================================================
  // TEACHER MANAGEMENT API
  // ============================================================================
  
  /**
   * Get pending teachers
   * GET /api/admin/teachers/pending
   * Purpose: List teachers awaiting approval
   * @returns {Promise} Response with pending teachers array
   */
  getPendingTeachers: async () => {
    const response = await api.get('/admin/teachers/pending');
    return response.data;
  },

  // ============================================================================
  // SYSTEM CONFIGURATION API
  // ============================================================================
  
  /**
   * Get subjects
   * GET /api/admin/subjects
   * Purpose: List all subjects in system
   * @returns {Promise} Response with subjects array
   */
  getSubjects: async () => {
    const response = await api.get('/admin/subjects');
    return response.data;
  },

  /**
   * Get grades
   * GET /api/admin/grades
   * Purpose: List all grades in system
   * @returns {Promise} Response with grades array
   */
  getGrades: async () => {
    const response = await api.get('/admin/grades');
    return response.data;
  },

  /**
   * Approve teacher
   * PUT /api/admin/teachers/:teacherId/status
   * Purpose: Approve teacher registration
   * @param {number} teacherId - ID of teacher to approve
   * @param {Object} data - Additional approval data
   * @returns {Promise} Response confirming approval
   */
  approveTeacher: async (teacherId, data) => {
    return adminService.updateTeacherStatus(teacherId, 'approved', data);
  },

  /**
   * Update teacher status (unified method)
   * PUT /api/admin/teachers/:teacherId/status
   * Purpose: Update teacher status (approved/rejected/pending/etc.)
   * @param {number} teacherId - ID of teacher
   * @param {string} status - New status (approved, rejected, pending, etc.)
   * @param {Object} data - Additional data for the status update
   * @returns {Promise} Response with updated status
   */
  updateTeacherStatus: async (teacherId, status, data = {}) => {
    const response = await api.put(`/admin/teachers/${teacherId}/status`, { status, ...data });
    return response.data;
  },

  /**
   * Get teacher subjects
   * GET /api/admin/teachers/:teacherId/subjects
   * Purpose: List subjects assigned to specific teacher
   * @param {number} teacherId - ID of the teacher
   * @returns {Promise} Response with teacher subjects array
   */
  getTeacherSubjects: async (teacherId) => {
    const response = await api.get(`/admin/teachers/${teacherId}/subjects`);
    return response.data;
  },

  /**
   * Reject teacher
   * PUT /api/admin/teachers/:teacherId/status
   * Purpose: Reject teacher registration
   * @param {number} teacherId - ID of teacher to reject
   * @param {Object} data - Rejection reason/data
   * @returns {Promise} Response confirming rejection
   */
  rejectTeacher: async (teacherId, data) => {
    return adminService.updateTeacherStatus(teacherId, 'rejected', data);
  },

  /**
   * Get user details
   * GET /api/admin/users/:userId
   * Purpose: Get detailed information about a specific user
   * @param {number} userId - ID of the user
   * @returns {Promise} Response with user details
   */
  getUser: async (userId) => {
    const response = await api.get(`/admin/users/${userId}`);
    return response.data;
  },

  /**
   * Update user status
   * PUT /api/admin/users/:userId/status
   * Purpose: Update user active status (active/inactive)
   * @param {number} userId - ID of the user
   * @param {Object} data - Status data (status, isActive)
   * @returns {Promise} Response with updated user
   */
  updateUserStatus: async (userId, data) => {
    const response = await api.put(`/admin/users/${userId}/status`, data);
    return response.data;
  },

  /**
   * Delete user
   * DELETE /api/admin/users/:userId
   * Purpose: Remove user from system
   * @param {number} userId - ID of the user to delete
   * @returns {Promise} Response confirming deletion
   */
  deleteUser: async (userId) => {
    const response = await api.delete(`/admin/users/${userId}`);
    return response.data;
  },

  /**
   * Delete notification
   * DELETE /api/admin/notifications/:notificationId
   * Purpose: Remove notification from system
   * @param {number} notificationId - ID of notification
   * @returns {Promise} Response confirming deletion
   */
  deleteNotification: async (notificationId) => {
    const response = await api.delete(`/admin/notifications/${notificationId}`);
    return response.data;
  },

  // ============================================================================
  // ACTIVITIES API
  // ============================================================================
  
  /**
   * Get admin activities
   * GET /api/admin/activities
   * Purpose: List system activities with filtering
   * @param {Object} params - Query parameters for filtering
   * @returns {Promise} Response with activities array
   */
  getActivities: async (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key]) queryParams.append(key, params[key]);
    });
    const response = await api.get(`/admin/activities?${queryParams}`);
    return response.data;
  },

  /**
   * Create activity and send notifications
   * POST /api/admin/activities
   * Purpose: Create new system activity
   * @param {Object} activityData - Activity details (type, details, relatedId)
   * @returns {Promise} Response with created activity
   */
  createActivity: async (activityData) => {
    const response = await api.post('/admin/activities', activityData);
    return response.data;
  },

  // ============================================================================
  // STATISTICS API
  // ============================================================================
  
  /**
   * Get admin statistics
   * GET /api/admin/stats
   * Purpose: Get general system statistics
   * @returns {Promise} Response with stats data
   */
  getStats: async () => {
    const response = await api.get('/admin/stats');
    return response.data;
  },

  // ============================================================================
  // COURSES MANAGEMENT API
  // ============================================================================
  
  /**
   * Get all courses with filters
   * GET /api/admin/courses
   * Purpose: List all courses with filtering options
   * @param {Object} params - Query parameters (search, subjectId, gradeId, status)
   * @returns {Promise} Response with courses array
   */
  getCourses: async (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key]) queryParams.append(key, params[key]);
    });
    const response = await api.get(`/admin/courses?${queryParams}`);
    return response.data;
  },

  /**
   * Get single course
   * GET /api/admin/courses/:courseId
   * Purpose: Get detailed course information for editing
   * @param {number} courseId - ID of course to fetch
   * @returns {Promise} Response with course data
   */
  getCourse: async (courseId) => {
    const response = await api.get(`/admin/courses/${courseId}`);
    return response.data;
  },

  /**
   * Create new course
   * POST /api/admin/courses
   * Purpose: Add new course to system
   * @param {Object} courseData - Course information
   * @returns {Promise} Response with created course
   */
  createCourse: async (courseData) => {
    const response = await api.post('/admin/courses', courseData);
    return response.data;
  },

  /**
   * Update course (general)
   * PUT /api/admin/courses/:courseId
   * Purpose: Modify existing course information
   * @param {number} courseId - ID of course to update
   * @param {Object} courseData - Updated course information
   * @returns {Promise} Response with updated course
   */
  updateCourse: async (courseId, courseData) => {
    const response = await api.put(`/admin/courses/${courseId}`, courseData);
    return response.data;
  },

  /**
   * Update course status
   * PUT /api/admin/courses/:courseId/status
   * Purpose: Change course active status
   * @param {number} courseId - ID of course
   * @param {Object} statusData - Status update data (isActive)
   * @returns {Promise} Response confirming status update
   */
  updateCourseStatus: async (courseId, statusData) => {
    const response = await api.put(`/admin/courses/${courseId}/status`, statusData);
    return response.data;
  },

  // ============================================================================
  // LESSONS MANAGEMENT API
  // ============================================================================
  
  /**
   * Get all lessons with filters
   * GET /api/admin/lessons
   * Purpose: List all lessons with filtering options
   * @param {Object} params - Query parameters (search, subjectId, gradeId, status)
   * @returns {Promise} Response with lessons array
   */
  getLessons: async (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key]) queryParams.append(key, params[key]);
    });
    const response = await api.get(`/admin/lessons?${queryParams}`);
    return response.data;
  },

  /**
   * Get single lesson
   * GET /api/admin/lessons/:lessonId
   * Purpose: Get detailed lesson information for editing
   * @param {number} lessonId - ID of lesson to fetch
   * @returns {Promise} Response with lesson data
   */
  getLesson: async (lessonId) => {
    const response = await api.get(`/admin/lessons/${lessonId}`);
    return response.data;
  },

  /**
   * Create new lesson
   * POST /api/admin/lessons
   * Purpose: Add new lesson to system
   * @param {Object} lessonData - Lesson information
   * @returns {Promise} Response with created lesson
   */
  createLesson: async (lessonData) => {
    const response = await api.post('/admin/lessons', lessonData);
    return response.data;
  },

  /**
   * Update lesson (general)
   * PUT /api/admin/lessons/:lessonId
   * Purpose: Modify existing lesson information
   * @param {number} lessonId - ID of lesson to update
   * @param {Object} lessonData - Updated lesson information
   * @returns {Promise} Response with updated lesson
   */
  updateLesson: async (lessonId, lessonData) => {
    const response = await api.put(`/admin/lessons/${lessonId}`, lessonData);
    return response.data;
  },

  /**
   * Update lesson status
   * PUT /api/admin/lessons/:lessonId/status
   * Purpose: Change lesson active status
   * @param {number} lessonId - ID of lesson
   * @param {Object} statusData - Status update data (isActive)
   * @returns {Promise} Response confirming status update
   */
  updateLessonStatus: async (lessonId, statusData) => {
    const response = await api.put(`/admin/lessons/${lessonId}/status`, statusData);
    return response.data;
  },

  // ============================================================================
  // STAGES MANAGEMENT API
  // ============================================================================
  
  /**
   * Get all stages
   * GET /api/admin/stages
   * Purpose: List all educational stages
   * @returns {Promise} Response with stages array
   */
  getStages: async () => {
    const response = await api.get('/admin/stages');
    return response.data;
  },

  /**
   * Create new stage
   * POST /api/admin/stages
   * Purpose: Add new educational stage
   * @param {Object} stageData - Stage information
   * @returns {Promise} Response with created stage
   */
  createStage: async (stageData) => {
    const response = await api.post('/admin/stages', stageData);
    return response.data;
  },

  /**
   * Update stage
   * PUT /api/admin/stages/:stageId
   * Purpose: Modify existing stage information
   * @param {number} stageId - ID of stage to update
   * @param {Object} stageData - Updated stage information
   * @returns {Promise} Response with updated stage
   */
  updateStage: async (stageId, stageData) => {
    const response = await api.put(`/admin/stages/${stageId}`, stageData);
    return response.data;
  },

  /**
   * Delete stage
   * DELETE /api/admin/stages/:stageId
   * Purpose: Remove stage from system
   * @param {number} stageId - ID of stage to delete
   * @returns {Promise} Response confirming deletion
   */
  deleteStage: async (stageId) => {
    const response = await api.delete(`/admin/stages/${stageId}`);
    return response.data;
  },

  // ============================================================================
  // GRADES MANAGEMENT API
  // ============================================================================
  
  /**
   * Create new grade
   * POST /api/admin/grades
   * Purpose: Add new educational grade
   * @param {Object} gradeData - Grade information
   * @returns {Promise} Response with created grade
   */
  createGrade: async (gradeData) => {
    const response = await api.post('/admin/grades', gradeData);
    return response.data;
  },

  /**
   * Update grade
   * PUT /api/admin/grades/:gradeId
   * Purpose: Modify existing grade information
   * @param {number} gradeId - ID of grade to update
   * @param {Object} gradeData - Updated grade information
   * @returns {Promise} Response with updated grade
   */
  updateGrade: async (gradeId, gradeData) => {
    const response = await api.put(`/admin/grades/${gradeId}`, gradeData);
    return response.data;
  },

  /**
   * Delete grade
   * DELETE /api/admin/grades/:gradeId
   * Purpose: Remove grade from system
   * @param {number} gradeId - ID of grade to delete
   * @returns {Promise} Response confirming deletion
   */
  deleteGrade: async (gradeId) => {
    const response = await api.delete(`/admin/grades/${gradeId}`);
    return response.data;
  },

  // ============================================================================
  // SUBJECTS MANAGEMENT API
  // ============================================================================
  
  /**
   * Create new subject
   * POST /api/admin/subjects
   * Purpose: Add new subject to system
   * @param {Object} subjectData - Subject information
   * @returns {Promise} Response with created subject
   */
  createSubject: async (subjectData) => {
    const response = await api.post('/admin/subjects', subjectData);
    return response.data;
  },

  /**
   * Update subject
   * PUT /api/admin/subjects/:subjectId
   * Purpose: Modify existing subject information
   * @param {number} subjectId - ID of subject to update
   * @param {Object} subjectData - Updated subject information
   * @returns {Promise} Response with updated subject
   */
  updateSubject: async (subjectId, subjectData) => {
    const response = await api.put(`/admin/subjects/${subjectId}`, subjectData);
    return response.data;
  },

  /**
   * Delete subject
   * DELETE /api/admin/subjects/:subjectId
   * Purpose: Remove subject from system
   * @param {number} subjectId - ID of subject to delete
   * @returns {Promise} Response confirming deletion
   */
  deleteSubject: async (subjectId) => {
    const response = await api.delete(`/admin/subjects/${subjectId}`);
    return response.data;
  },

  // ============================================================================
  // TEACHERS MANAGEMENT API
  // ============================================================================
  
  /**
   * Get all teachers with filters
   * GET /api/admin/teachers
   * Purpose: List all teachers with filtering options
   * @param {Object} params - Query parameters (status, search, page, limit)
   * @returns {Promise} Response with teachers array
   */
  getAllTeachers: async (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key]) queryParams.append(key, params[key]);
    });
    const response = await api.get(`/admin/teachers?${queryParams}`);
    return response.data;
  },

  /**
   * Update user role
   * PUT /api/admin/users/:userId/role
   * Purpose: Change user's role (admin/teacher/student)
   * @param {number} userId - ID of the user
   * @param {Object} roleData - Role update data (role)
   * @returns {Promise} Response confirming role update
   */
  updateUserRole: async (userId, roleData) => {
    const response = await api.put(`/admin/users/${userId}/role`, roleData);
    return response.data;
  },

  // ============================================================================
  // SYSTEM SETTINGS API
  // ============================================================================

  /**
   * Get system settings
   * GET /api/admin/settings
   * Purpose: Retrieve all system settings
   * @returns {Promise} Response with settings object
   */
  getSettings: async () => {
    const response = await api.get('/admin/settings');
    return response.data;
  },

  /**
   * Update system settings
   * PUT /api/admin/settings
   * Purpose: Update one or more system settings
   * @param {Object} settingsData - Object with key-value pairs to update
   * @returns {Promise} Response confirming settings update
   */
  updateSettings: async (settingsData) => {
    const response = await api.put('/admin/settings', settingsData);
    return response.data;
  }
};

export default adminService;
